import { action, computed } from 'mobx';

import { AppManager, Variables, Input } from 'core';

import { PaymentSpaceModel, PaymentSpaceApp } from 'apps/payment/app';

import {
  InitiativePreviewSubscriptionQuery,
  ProfilePreviewSubscriptionQuery,
  CreateSubscriptionMutation,
  CancelSubscriptionMutation,
  AddSubscriptionSeatsMutation,
  RemoveSubscriptionSeatsMutation,
  ProfileAlterSeatsPreviewQuery,
  OrganizationAlterSeatsPreviewQuery,
} from 'apps/payment/gql';

export class SubscriptionAppManager extends AppManager {
  static registryName = 'SubscriptionAppManager';
  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('paymentSpace', PaymentSpaceApp);
    this.AppRef('paymentSpacePreview', PaymentSpaceApp);

    this.variables = new Variables({
      spaceSlug: () => this.spaceSlug,
    });

    this.previewVariables = new Variables({
      spaceSlug: () => this.spaceSlug,
      coupon: () => (this.paymentSpace ? this.paymentSpace.coupon : ''),
    });

    this.variables.onChange(['spaceSlug'], this.NewSubscriptionQuery);
  }

  @computed get loading() {
    const subscriptionQuery = this.queries.get('subscription');
    return subscriptionQuery ? subscriptionQuery.loading : true;
  }

  @computed get loadingPreview() {
    const previewQuery = this.queries.get('preview');
    return previewQuery ? previewQuery.loading : true;
  }

  @action NewSubscriptionQuery = (options = { preview: false }) => {
    const { preview } = options;
    if (!this.variables.spaceSlug) return null;
    let PreviewSubscriptionQuery = null;

    if (this.variables.spaceSlug.startsWith('+')) {
      PreviewSubscriptionQuery = InitiativePreviewSubscriptionQuery;
    } else if (this.variables.spaceSlug.startsWith('@')) {
      PreviewSubscriptionQuery = ProfilePreviewSubscriptionQuery;
    }
    if (!PreviewSubscriptionQuery) return null;

    const q = new PreviewSubscriptionQuery(this, {
      queryKey: preview ? 'preview' : 'subscription',
      fetchPolicy: 'no-cache',
      variables: preview ? this.previewVariables : this.variables,
    });
    q.process(
      result => (result ? PaymentSpaceModel.Load(result) : null),
      preview ? this.UpdatePaymentSpacePreview : this.UpdatePaymentSpace,
    );
    q.query();
    return q;
  };

  @action UpdatePaymentSpace = paymentSpaceModel => {
    this.paymentSpace = paymentSpaceModel;
  };

  @action UpdatePaymentSpacePreview = paymentSpaceModel => {
    if (this.paymentSpacePreview && paymentSpaceModel) {
      this.paymentSpacePreview.setUpstream(paymentSpaceModel);
    } else if (paymentSpaceModel) {
      this.paymentSpacePreview = paymentSpaceModel;
    }
    if (this.paymentSpacePreview && !this.paymentSpacePreview.form) {
      this.paymentSpacePreview.InitVATForm();
    }
  };

  @action SubmitSubscription = (paymentSpace, options) => {
    const subscriptionInput = new Input({
      spaceSlug: () => paymentSpace.spaceSlug,
      countryCode: () => paymentSpace.countryCode,
      taxId: () => paymentSpace.taxId,
      coupon: () => paymentSpace.coupon,
      extraSeats: () => paymentSpace.extraSeats || 0,
    });
    const mutation = CreateSubscriptionMutation.New(subscriptionInput, options);
    return mutation;
  };

  @action CancelSubscription = (paymentSpace, options = {}) => {
    const input = new Input({
      subscriptionId: paymentSpace.subscription.id,
    });
    const mutation = CancelSubscriptionMutation.New(input, options);
    return mutation;
  };

  @action NewPreviewExtraSeatsQuery = extraSeats => {
    const { spaceSlug, coupon } = this.variables;
    if (!spaceSlug) return null;
    let PreviewSubscriptionQuery = null;
    const variables = new Variables({ spaceSlug, coupon, extraSeats });
    if (spaceSlug.startsWith('+')) {
      PreviewSubscriptionQuery = InitiativePreviewSubscriptionQuery;
    } else if (spaceSlug.startsWith('@')) {
      PreviewSubscriptionQuery = ProfilePreviewSubscriptionQuery;
    }
    if (!PreviewSubscriptionQuery) return null;

    const q = new PreviewSubscriptionQuery(this, {
      queryKey: 'preview',
      fetchPolicy: 'no-cache',
      variables,
    });
    q.process(result => {
      if (result && result.subscriptionPreview) {
        return PaymentSpaceModel.Load(result);
      }
      return null;
    }, this.UpdatePaymentSpacePreview);
    q.query();
    return q;
  };

  @action NewPreviewAlterSeatsQuery = (addedSeats, removedSeats) => {
    this.paymentSpacePreview = null;
    const variables = new Variables({
      spaceSlug: () => this.variables.spaceSlug,
      addedSeats: () => addedSeats || 0,
      removedSeats: () => removedSeats || 0,
    });

    let PreviewQuery = null;
    if (this.spaceSlug.startsWith('+')) {
      PreviewQuery = OrganizationAlterSeatsPreviewQuery;
    } else if (this.spaceSlug.startsWith('@')) {
      PreviewQuery = ProfileAlterSeatsPreviewQuery;
    }
    if (!PreviewQuery) return null;
    const alterSeatsPreviewQuery = new PreviewQuery(this, {
      variables,
      queryKey: 'preview',
      fetchPolicy: 'no-cache',
    });
    alterSeatsPreviewQuery.process(
      result => (result ? PaymentSpaceModel.Load(result) : null),
      this.UpdatePaymentSpacePreview,
    );
    alterSeatsPreviewQuery.query();
    return alterSeatsPreviewQuery;
  };

  @action SaveAlterSeats = (seats, options = {}) => {
    let input = null;
    let AlterSubscriptionSeatsMutation = null;
    if (seats.addSeatsAmount) {
      input = new Input({
        subscriptionId: this.paymentSpace.subscription.id,
        addNSeats: seats.addSeatsAmount,
      });
      AlterSubscriptionSeatsMutation = AddSubscriptionSeatsMutation;
    } else if (seats.removeSeatsAmount) {
      input = new Input({
        subscriptionId: this.paymentSpace.subscription.id,
        removeNSeats: seats.removeSeatsAmount,
      });
      AlterSubscriptionSeatsMutation = RemoveSubscriptionSeatsMutation;
    }

    if (!(AlterSubscriptionSeatsMutation && input)) return null;
    const mutation = AlterSubscriptionSeatsMutation.New(input, options);
    return mutation;
  };
}
